import { useState, useMemo } from "react";
import { useApp } from "@/context/AppContext";
import { SectionHeader } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { Slider } from "@/components/ui/slider";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Sliders, Zap, IndianRupee, Leaf, Clock, TrendingUp } from "lucide-react";

const LCOE = 1.85, YIELD = 1500, DEGRADE = 0.007, ESCALATION = 0.035, CAPEX_PER_KWP = 40000, CO2_PER_KWH = 0.82;

const CHART_MARGIN = { left: -10, right: 8, top: 8 };
const AXIS_TICK = { fill: "#64748b", fontSize: 11 };
const TOOLTIP_STYLE = { background: "#FFFFFF", border: "1px solid #BFE3E0", borderRadius: 12, color: "#0B3B39" };

function inr(n) {
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(2)} L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(1)} K`;
  return `₹${Math.round(n)}`;
}

export default function SolarCalculator() {
  const { t } = useApp();
  const cx = t.calcX;
  const [capacity, setCapacity] = useState(500);
  const [tariff, setTariff] = useState(8.2);
  const [years, setYears] = useState(25);

  const model = useMemo(() => {
    const capex = capacity * CAPEX_PER_KWP;
    let cumulative = 0, payback = null;
    const rows = [];
    let annualGenY1 = capacity * YIELD;
    for (let y = 1; y <= years; y++) {
      const gen = capacity * YIELD * Math.pow(1 - DEGRADE, y - 1);
      const gridTariff = tariff * Math.pow(1 + ESCALATION, y - 1);
      const saving = gen * (gridTariff - LCOE);
      cumulative += saving;
      const net = cumulative - capex;
      if (payback === null && net >= 0) payback = y - (net / saving);
      rows.push({ year: `Y${y}`, savings: Math.round(saving), cumulative: Math.round(net) });
    }
    const totalSavings = cumulative;
    const co2 = (annualGenY1 * CO2_PER_KWH) / 1000;
    return { capex, totalSavings, payback, rows, annualGenY1, co2 };
  }, [capacity, tariff, years]);

  const outputs = [
    { icon: Zap, label: t.calc.annualGen, value: `${(model.annualGenY1 / 100000).toFixed(2)} L kWh`, sub: cx.year1 },
    { icon: IndianRupee, label: t.calc.savings25, value: inr(model.totalSavings), sub: cx.overYears.replace("{years}", years) },
    { icon: Clock, label: t.calc.payback, value: model.payback ? `${model.payback.toFixed(1)} ${cx.yrs}` : "> " + years + ` ${cx.yrs}`, sub: cx.breakeven },
    { icon: Leaf, label: t.calc.co2, value: `${Math.round(model.co2)} MT`, sub: cx.annualAbatement },
  ];

  return (
    <div>
      <section className="relative border-b border-teal-900/40 bg-[#E9F5F4]/40 py-14">
        <div className="oj-container">
          <div className="oj-eyebrow mb-3"><Sliders className="w-4 h-4" /> {cx.engine}</div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#0B3B39] tracking-tight">{t.calc.title}</h1>
          <p className="mt-4 text-slate-600 max-w-3xl leading-relaxed">{t.calc.subtitle}</p>
        </div>
      </section>

      <section className="oj-container py-12 grid grid-cols-1 lg:grid-cols-5 gap-8">
        {/* INPUTS */}
        <Reveal className="lg:col-span-2">
          <div className="oj-card p-7">
            <h3 className="oj-h3 mb-6 flex items-center gap-2"><Sliders className="w-5 h-5 text-teal-600" /> {cx.params}</h3>
            <div className="space-y-8">
              <SliderRow label={t.calc.capacity} value={`${capacity.toLocaleString()} kWp`} min={100} max={5000} step={50} val={capacity} onChange={setCapacity} testid="capacity" />
              <SliderRow label={t.calc.tariff} value={`₹${tariff.toFixed(1)}`} min={5} max={12} step={0.1} val={tariff} onChange={setTariff} testid="tariff" />
              <SliderRow label={t.calc.years} value={`${years} ${cx.yearsUnit}`} min={10} max={25} step={1} val={years} onChange={setYears} testid="years" />
            </div>
            <div className="mt-8 pt-6 border-t border-teal-900/50 flex items-center justify-between">
              <span className="text-sm text-slate-600">{cx.estCapex}</span>
              <span className="text-xl font-bold gold-text font-mono" data-testid="calc-capex">{inr(model.capex)}</span>
            </div>
          </div>
        </Reveal>

        {/* OUTPUTS */}
        <div className="lg:col-span-3 space-y-6">
          <div className="grid grid-cols-2 gap-4">
            {outputs.map((o, i) => {
              const Icon = o.icon;
              return (
                <Reveal key={o.label} delay={i * 0.07}>
                  <div className="oj-card p-5 h-full" data-testid={`calc-output-${i}`}>
                    <Icon className="w-6 h-6 text-teal-600 mb-3" />
                    <div className="text-xs font-mono uppercase tracking-wider text-slate-500">{o.label}</div>
                    <div className="mt-1 text-2xl font-extrabold text-white">{o.value}</div>
                    <div className="text-xs text-teal-400/70 mt-0.5">{o.sub}</div>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <Reveal delay={0.2}>
            <div className="oj-card p-6">
              <div className="flex items-center gap-2 mb-4"><TrendingUp className="w-5 h-5 text-amber-400" /><h4 className="font-bold text-white">{cx.cashflow}</h4></div>
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={model.rows} margin={CHART_MARGIN}>
                  <defs>
                    <linearGradient id="cf" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="#f59e0b" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(1,116,113,0.12)" />
                  <XAxis dataKey="year" tick={AXIS_TICK} interval={Math.floor(years / 8)} />
                  <YAxis tick={AXIS_TICK} tickFormatter={(v) => inr(v)} width={70} />
                  <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v) => inr(v)} />
                  <Area type="monotone" dataKey="cumulative" stroke="#f59e0b" strokeWidth={2.5} fill="url(#cf)" name={cx.netCashflow} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

function SliderRow({ label, value, min, max, step, val, onChange, testid }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <label className="text-sm font-medium text-slate-600">{label}</label>
        <span className="text-sm font-mono font-semibold text-[#015E5C] px-2.5 py-1 rounded-md bg-teal-100/70 border border-teal-300/60">{value}</span>
      </div>
      <Slider value={[val]} min={min} max={max} step={step} onValueChange={(v) => onChange(v[0])} data-testid={`calc-slider-${testid}`} />
    </div>
  );
}
